import { logger } from '../logger';

/**
 * Custom JSON:API Deserializer utility
 * Handles transformation from JSON:API format to flat objects
 */

/**
 * Convert snake_case keys to camelCase recursively
 * Preserves specific keys that should remain snake_case
 */
function convertKeysToCamelCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (!obj || typeof obj !== 'object') return obj;
  
  // Keys that should be preserved as snake_case
  const preserveKeys = ['supported_languages', 'name_translations', 'description_translations', 'rate_limits', 'authentication_type'];
  
  const result: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    // Check if this key should be preserved
    const shouldPreserve = preserveKeys.includes(key);
    const finalKey = shouldPreserve ? key : key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    const value = obj[key];
    
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[finalKey] = convertKeysToCamelCase(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[finalKey] = value.map(item => 
        typeof item === 'object' && item !== null ? convertKeysToCamelCase(item as Record<string, unknown>) : item
      );
    } else {
      result[finalKey] = value;
    }
  });
  
  return result;
}

/**
 * Transform a single JSON:API resource to flat object
 * @param resource - JSON:API resource with type, id, and attributes
 * @returns Flat object with id and all attributes at root level
 */
export function deserializeResource(resource: Record<string, unknown>): Record<string, unknown> {
  if (!resource || typeof resource !== 'object') {
    return resource;
  }

  // If it's already a flat object (not JSON:API), return as-is
  if (!resource.type || !resource.attributes) {
    return resource;
  }

  // Transform JSON:API resource to flat object
  // Convert attributes to camelCase first
  const camelCaseAttributes = convertKeysToCamelCase(resource.attributes as Record<string, unknown>);
  const result: Record<string, unknown> = {
    id: resource.id,
    ...camelCaseAttributes,
  };

  // Handle specific field transformations for one-time plans
  if (resource.type === 'one-time-plans') {
    // Map planType to type for frontend compatibility (if needed)
    if (result.planType && !result.type) {
      result.type = result.planType;
    }
    // Transform lifetime plans to have accessDurationMonths: 'unlimited'
    if (result.isLifetime === true) {
      result.accessDurationMonths = 'unlimited';
    }
  }

  // Handle JSON string fields that need parsing
  const jsonStringFields = ['tags', 'supported_types'];
  jsonStringFields.forEach(field => {
    if (result[field] && typeof result[field] === 'string') {
      try {
        result[field] = JSON.parse(result[field] as string);
      } catch (error) {
        console.warn(`Failed to parse JSON field ${field}:`, result[field], error);
        // Keep as string if parsing fails
      }
    }
  });

  // Map authentication.supported_types to authentication_type for frontend compatibility
  if (result.authentication && typeof result.authentication === 'object') {
    const auth = result.authentication as { supported_types?: string[]; required?: boolean };
    if (auth.supported_types && Array.isArray(auth.supported_types) && auth.supported_types.length > 0) {
      // Take the first supported type as the primary authentication type
      result.authentication_type = auth.supported_types[0];
    }
  }

  // Include relationships if present (flattened)
  if (resource.relationships && typeof resource.relationships === 'object') {
    const relationships = resource.relationships as Record<string, unknown>;
    Object.keys(relationships).forEach(key => {
      const relationship = relationships[key];
      
      // Convert kebab-case to camelCase for relationship keys
      // Special case: 'prices' should become 'pricesArray' to avoid conflict
      let camelKey = key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      if (camelKey === 'prices') {
        camelKey = 'pricesArray';
      }
      
      // Handle different relationship formats
      if (relationship && typeof relationship === 'object') {
        const relObj = relationship as Record<string, unknown>;
        
        // Standard JSON:API relationship with data property
        if (relObj.data !== undefined) {
          const relData = relObj.data;
          
          // Handle specific relationship transformations for prices
          if (key === 'prices' && Array.isArray(relData)) {
            // Transform prices array to MultiCurrencyPrice object
            const pricesObj: Record<string, number> = {};
            relData.forEach((priceItem: unknown) => {
              if (typeof priceItem === 'object' && priceItem !== null) {
                const price = priceItem as Record<string, unknown>;
                
                // Handle both formats:
                // 1. Direct format: {currency: "USD", amount: 12}
                // 2. JSON:API format: {attributes: {currency: "USD", base_price: 12}}
                
                if (price.currency && typeof price.currency === 'string') {
                  // Direct format from backend
                  const amount = price.amount ?? price.base_price;
                  if (typeof amount === 'number') {
                    pricesObj[price.currency] = amount;
                  }
                } else if (price.attributes && typeof price.attributes === 'object') {
                  // JSON:API format (for backward compatibility)
                  const attrs = price.attributes as Record<string, unknown>;
                  if (attrs.currency && typeof attrs.currency === 'string') {
                    const amount = attrs.amount ?? attrs.base_price;
                    if (typeof amount === 'number') {
                      pricesObj[attrs.currency] = amount;
                    }
                  }
                }
              }
            });
            
            // Set both prices and pricesArray for compatibility
            if (Object.keys(pricesObj).length > 0) {
              result.prices = pricesObj;
              // Also keep the original array format for debugging
              result[camelKey] = relData.map(item => {
                if (typeof item === 'object' && item !== null) {
                  return convertKeysToCamelCase(item as Record<string, unknown>);
                }
                return item;
              });
            } else {
              // If no prices were parsed, keep the raw data for debugging
              result[camelKey] = relData;
            }
          } else if (key === 'features' && Array.isArray(relData)) {
            // Transform features array to string array
            const featuresArray: string[] = [];
            relData.forEach((featureItem: unknown) => {
              if (typeof featureItem === 'object' && featureItem !== null) {
                const feature = featureItem as Record<string, unknown>;
                if (feature.attributes && typeof feature.attributes === 'object') {
                  const attrs = feature.attributes as Record<string, unknown>;
                  if (attrs.name && typeof attrs.name === 'string') {
                    featuresArray.push(attrs.name);
                  } else if (attrs.key && typeof attrs.key === 'string') {
                    featuresArray.push(attrs.key);
                  }
                }
              }
            });
            result.features = featuresArray;
            result[camelKey] = relData; // Keep original for compatibility
          } else {
            result[camelKey] = relData;
          }

          if (relObj.meta && typeof relObj.meta === 'object') {
            const metaKey = `${camelKey}Meta`;
            result[metaKey] = convertKeysToCamelCase(relObj.meta as Record<string, unknown>);
          }

          if (relObj.links && typeof relObj.links === 'object') {
            const linksKey = `${camelKey}Links`;
            result[linksKey] = convertKeysToCamelCase(relObj.links as Record<string, unknown>);
          }
        } 
        // Special handling for feature_flags or feature-flags
        else if (key === 'feature_flags' || key === 'feature-flags') {
          // Feature flags are returned as a direct object { flagKey: boolean }
          // Store them at the root level as featureFlags
          result.featureFlags = relationship as Record<string, boolean>;
        }
        // Hybrid format - relationship contains the data directly
        else if (Array.isArray(relationship)) {
          // Direct array (like prices)
          result[camelKey] = relationship.map(item => {
            if (typeof item === 'object' && item !== null) {
              // Convert snake_case keys to camelCase in nested objects
              return convertKeysToCamelCase(item as Record<string, unknown>);
            }
            return item;
          });
        } else {
          // Direct object (like credit_settings)
          result[camelKey] = convertKeysToCamelCase(relationship as Record<string, unknown>);
        }
      } else {
        // Primitive or null
        result[camelKey] = relationship;
      }
    });
  }

  // Include meta and links if present (with prefixes to avoid conflicts)
  if (resource.meta) {
    result._meta = resource.meta;
  }
  if (resource.links) {
    result._links = resource.links;
  }

  return result;
}

/**
 * Deserialize JSON:API response to flat objects
 * @param jsonApiData - JSON:API formatted data
 * @returns Deserialized data with flat structure
 */
export function deserializeJsonApi<T = Record<string, unknown>>(jsonApiData: unknown): T {
  try {
    logger.debug('🔄 Deserializing JSON:API data:', jsonApiData);
    
    // Check if it's already in the expected format (not JSON:API)
    if (!jsonApiData || typeof jsonApiData !== 'object') {
      return jsonApiData as T;
    }

    // Type guard for JSON:API data
    const data = jsonApiData as Record<string, unknown>;
    
    // If it has a 'data' property, it might be JSON:API format
    if (data.data !== undefined) {
      // Handle single resource or array of resources
      if (Array.isArray(data.data)) {
        // Array of resources
        const deserializedArray = (data.data as Record<string, unknown>[]).map(item => deserializeResource(item));
        
        logger.debug('✅ Deserialized JSON:API array:', deserializedArray);
        
        // Return in expected API response format
        const meta = data.meta as Record<string, unknown> | undefined;
        return {
          data: deserializedArray,
          // Include meta if present
          ...(meta && { meta }),
          // Map pagination info to expected format
          total: (meta?.total as number) || deserializedArray.length,
          page: (meta?.current_page as number) || 1,
          per_page: (meta?.per_page as number) || deserializedArray.length,
        } as T;
      } else {
        // Single resource in JSON:API format
        const deserialized = deserializeResource(data.data as Record<string, unknown>);
        logger.debug('✅ Deserialized JSON:API single resource:', deserialized);
        // Return the deserialized object directly (LiveAdapter will wrap it)
        return deserialized as T;
      }
    }

    // If it's a direct JSON:API resource (has type and attributes)
    if (data.type && data.attributes) {
      const deserialized = deserializeResource(data);
      logger.debug('✅ Deserialized direct JSON:API resource:', deserialized);
      return deserialized as T;
    }

    // Not JSON:API format, return as-is
    logger.debug('✅ Data is not JSON:API format, returning as-is');
    return jsonApiData as T;
  } catch (error) {
    logger.error('❌ Error deserializing JSON:API data:', error);
    logger.error('❌ Original data:', jsonApiData);
    // Fall back to returning the original data
    return jsonApiData as T;
  }
}

/**
 * Check if data is in JSON:API format
 */
export function isJsonApiFormat(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  
  const jsonData = data as Record<string, unknown>;
  
  // Check for JSON:API structure
  if (jsonData.data !== undefined) {
    const dataToCheck = Array.isArray(jsonData.data) ? (jsonData.data as unknown[])[0] : jsonData.data;
    const dataObj = dataToCheck as Record<string, unknown>;
    return Boolean(dataObj && typeof dataObj === 'object' && dataObj.type && dataObj.attributes);
  }
  
  return Boolean(jsonData.type && jsonData.attributes);
}

/**
 * Transform JSON:API resource to flat object manually (fallback)
 */
export function manualJsonApiTransform(resource: Record<string, unknown>): Record<string, unknown> {
  if (!resource || !resource.type || !resource.attributes) {
    return resource;
  }
  
  const result: Record<string, unknown> = {
    id: resource.id,
  };
  
  // Spread attributes
  if (resource.attributes && typeof resource.attributes === 'object') {
    Object.assign(result, resource.attributes as Record<string, unknown>);
  }
  
  // Include meta and links if present
  if (resource.meta) {
    result._meta = resource.meta;
  }
  if (resource.links) {
    result._links = resource.links;
  }
  
  return result;
}
